import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { FileUpload } from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, fmtInt, todayISO } from "@/lib/format";
import { downloadPdf, openFile } from "@/lib/files";
import { Plus, Truck, Loader2, Trash2, FileDown, Paperclip, Pencil } from "lucide-react";
import { toast } from "sonner";

const emptyLine = () => ({ produit_id: "", designation: "", quantite_commandee: 0, quantite_livree: 1, lot: "" });

export default function Livraisons() {
  const { societes, societeParam, societeId } = useApp();
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [produits, setProduits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/bons-livraison-client", { params: societeParam }); setItems(data.items); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const loadRefs = (sid) => {
    if (!sid) return;
    api.get("/clients", { params: { societe_id: sid } }).then((r) => setClients(r.data));
    api.get("/produits", { params: { societe_id: sid } }).then((r) => setProduits(r.data));
  };
  const openNew = () => {
    const sid = societeId !== "all" ? societeId : (societes[0]?.id || "");
    setForm({ societe_id: sid, client_id: "", date: todayISO(), chauffeur: "", vehicule: "", transporteur: "", receptionnaire: "", adresse_livraison: "", observation: "", signature: null, lignes: [emptyLine()] });
    setEditingId(null); loadRefs(sid); setOpen(true);
  };
  const openEdit = (b) => {
    setForm({
      societe_id: b.societe_id, client_id: b.client_id || "", date: b.date || todayISO(),
      chauffeur: b.chauffeur || "", vehicule: b.vehicule || "", transporteur: b.transporteur || "",
      receptionnaire: b.receptionnaire || "", adresse_livraison: b.adresse_livraison || "", observation: b.observation || "",
      signature: b.signature_id ? { id: b.signature_id, filename: b.signature_nom } : null,
      lignes: (b.lignes && b.lignes.length ? b.lignes : [emptyLine()]).map((l) => ({
        produit_id: l.produit_id || "", designation: l.designation || "",
        quantite_commandee: l.quantite_commandee || 0, quantite_livree: l.quantite_livree ?? 1, lot: l.lot || "",
      })),
    });
    setEditingId(b.id); loadRefs(b.societe_id); setOpen(true);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], [k]: v }; return { ...f, lignes: l }; });
  const pickProduct = (i, pid) => { const p = produits.find((x) => x.id === pid); setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], produit_id: pid, designation: p?.nom || l[i].designation }; return { ...f, lignes: l }; }); };
  const addLine = () => setForm((f) => ({ ...f, lignes: [...f.lignes, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lignes: f.lignes.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.societe_id || !form.client_id) return toast.error("Société et client requis.");
    if (form.lignes.some((l) => !l.designation)) return toast.error("Chaque ligne doit avoir une désignation.");
    setSaving(true);
    try {
      const payload = {
        ...form, signature_id: form.signature?.id || null, signature_nom: form.signature?.filename || null,
        lignes: form.lignes.map((l) => ({ ...l, produit_id: l.produit_id || null, quantite_commandee: Number(l.quantite_commandee) || 0, quantite_livree: Number(l.quantite_livree) })),
      };
      if (editingId) {
        await api.put(`/bons-livraison-client/${editingId}`, payload);
        toast.success("Bon de livraison modifié.");
      } else {
        await api.post("/bons-livraison-client", payload);
        toast.success("Bon de livraison créé — stock mis à jour.");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Bons de livraison clients" subtitle="Livraisons, reliquats et sortie de stock automatique" testid="livraisons-page"
        actions={can("livraisons") ? <Button onClick={openNew} className="gap-2" data-testid="new-bl-btn"><Plus className="h-4 w-4" /> Nouveau BL</Button> : null} />

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState icon={Truck} title="Aucun bon de livraison" hint="Créez un BL client : le stock des produits livrés est décrémenté automatiquement." />
          : (
            <div className="overflow-x-auto">
              <Table data-testid="bl-table">
                <TableHeader><TableRow className="bg-muted/50"><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead>Facture liée</TableHead><TableHead>Chauffeur</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((b) => (
                    <TableRow key={b.id} data-testid={`bl-row-${b.id}`}>
                      <TableCell className="tabular text-sm font-medium">{b.numero}</TableCell>
                      <TableCell className="text-sm">{b.client_name}</TableCell>
                      <TableCell className="tabular text-sm">{fmtDate(b.date)}</TableCell>
                      <TableCell className="tabular text-sm text-muted-foreground" data-testid={`bl-facture-${b.id}`}>{b.facture_numero || "—"}</TableCell>
                      <TableCell className="text-sm">{b.chauffeur || "—"}{b.vehicule ? ` · ${b.vehicule}` : ""}</TableCell>
                      <TableCell><Badge variant="secondary" className={b.statut === "livre" ? "bg-success/15 text-success" : "bg-warning/15 text-warning"}>{b.statut === "livre" ? "Livré" : "Partiellement livré"}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {can("livraisons") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(b)} data-testid={`bl-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>}
                          {b.signature_id && <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => openFile(b.signature_id)} title="BL signé"><Paperclip className="h-4 w-4" /></Button>}
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(`/pdf/bl-client/${b.id}`, b.numero)} data-testid={`bl-pdf-${b.id}`}><FileDown className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bl-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingId ? "Modifier le bon de livraison" : "Nouveau bon de livraison"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Client *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setField("client_id", v)}>
                    <SelectTrigger data-testid="bl-client"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-popover max-h-64">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setField("date", v)} testid="bl-date" /></div>
                <div className="space-y-1.5"><Label>Réceptionnaire</Label><Input value={form.receptionnaire} onChange={(e) => setField("receptionnaire", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Chauffeur</Label><Input value={form.chauffeur} onChange={(e) => setField("chauffeur", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Véhicule</Label><Input value={form.vehicule} onChange={(e) => setField("vehicule", e.target.value)} /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Transporteur</Label><Input value={form.transporteur} onChange={(e) => setField("transporteur", e.target.value)} /></div>
              </div>
              <div className="rounded-lg border overflow-hidden">
                <div className="bg-muted/50 px-3 py-2 text-xs font-semibold grid grid-cols-12 gap-2"><span className="col-span-4">Désignation</span><span className="col-span-3">Produit</span><span className="col-span-2 text-right">Commandé</span><span className="col-span-1 text-right">Livré</span><span className="col-span-1">Lot</span><span className="col-span-1"></span></div>
                {form.lignes.map((l, i) => (
                  <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 items-center border-t">
                    <Input className="col-span-4 h-9" value={l.designation} onChange={(e) => setLine(i, "designation", e.target.value)} data-testid={`bl-line-desig-${i}`} />
                    <div className="col-span-3"><Select value={l.produit_id} onValueChange={(v) => pickProduct(i, v)}><SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger><SelectContent className="bg-popover max-h-56">{produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom} ({fmtInt(p.quantite)})</SelectItem>)}</SelectContent></Select></div>
                    <Input className="col-span-2 h-9 text-right" type="number" value={l.quantite_commandee} onChange={(e) => setLine(i, "quantite_commandee", e.target.value)} />
                    <Input className="col-span-1 h-9 text-right" type="number" value={l.quantite_livree} onChange={(e) => setLine(i, "quantite_livree", e.target.value)} data-testid={`bl-line-livre-${i}`} />
                    <Input className="col-span-1 h-9" value={l.lot} onChange={(e) => setLine(i, "lot", e.target.value)} />
                    <button className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center" onClick={() => removeLine(i)} disabled={form.lignes.length === 1}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={addLine} className="w-full text-sm text-primary py-2 border-t hover:bg-accent/40 flex items-center justify-center gap-1.5" data-testid="bl-add-line"><Plus className="h-4 w-4" /> Ajouter une ligne</button>
              </div>
              <div className="space-y-1.5"><Label>BL signé (scan / photo)</Label><FileUpload value={form.signature} onChange={(v) => setField("signature", v)} testid="bl-signature" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="bl-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
